import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, RotateCcw, Clock, MapPin, Package, AlertCircle, CheckCircle, IndianRupee } from 'lucide-react';
import Header from './components/Header25';
import Footer from './components/Footer25';

const ShippingAndExchange25 = () => {
    const navigate = useNavigate();

    const shippingCards = [
        {
            icon: Truck, title: 'Standard Delivery',
            detail: '5-7 Business Days',
            price: 'Free on orders above ₹2,000',
            sub: '₹199 for orders below ₹2,000'
        },
        {
            icon: Clock, title: 'Express Delivery',
            detail: '2-3 Business Days',
            price: '₹399',
            sub: 'Available for select PIN codes'
        },
        {
            icon: MapPin, title: 'Metro Cities',
            detail: '3-5 Business Days',
            price: 'Priority processing',
            sub: 'Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad'
        },
        {
            icon: Package, title: 'International',
            detail: '10-15 Business Days',
            price: 'Calculated at checkout',
            sub: 'Available for 30+ countries'
        },
    ];

    const exchangeSteps = [
        { num: '01', title: 'Initiate Request', desc: 'Contact us within 15 days of delivery via email or WhatsApp with your order number and reason for return.' },
        { num: '02', title: 'Get Approval', desc: 'Our team will review your request and send you a return authorization along with shipping instructions within 24 hours.' },
        { num: '03', title: 'Ship the Product', desc: 'Pack the item securely in its original packaging with all tags attached. Use the prepaid shipping label we provide.' },
        { num: '04', title: 'Receive Refund/Exchange', desc: 'Once we receive and inspect the return, your refund or exchange will be processed within 7-10 business days.' },
    ];

    const policies = [
        { icon: CheckCircle, text: 'Items must be unworn, unwashed, and in original condition', allowed: true },
        { icon: CheckCircle, text: 'Original tags and packaging must be intact', allowed: true },
        { icon: CheckCircle, text: 'Return request within 15 days of delivery', allowed: true },
        { icon: CheckCircle, text: 'Free return shipping on defective or incorrect items', allowed: true },
        { icon: AlertCircle, text: 'Custom-made or altered items cannot be returned', allowed: false },
        { icon: AlertCircle, text: 'Intimates and accessories are non-returnable', allowed: false },
        { icon: AlertCircle, text: 'Items damaged due to misuse are not eligible', allowed: false },
        { icon: AlertCircle, text: 'Sale items are final sale (exchange only)', allowed: false },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-shipping * { box-sizing: border-box; }
                .sahaj-shipping { font-family: 'IBM Plex Sans', sans-serif; background: #FFFFFF; color: #111111; }
                .sahaj-shipping-heading { font-family: 'Space Grotesk', sans-serif; }
            `}</style>
            <div className="sahaj-shipping" style={{ minHeight: '100vh' }}>
                <Header />

                {/* Hero */}
                <div style={{ background: '#111111', padding: '60px 0', marginTop: 80 }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <h1 className="sahaj-shipping-heading" style={{
                            fontSize: 48, fontWeight: 700, color: '#FFFFFF', margin: 0, letterSpacing: '-1px'
                        }}>
                            SHIPPING & EXCHANGE
                        </h1>
                        <p style={{ fontSize: 16, color: '#9CA3AF', marginTop: 12, fontWeight: 300 }}>
                            Everything you need to know about delivery and returns
                        </p>
                    </div>
                </div>

                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '60px 24px 80px' }}>
                    {/* Shipping Options */}
                    <h2 className="sahaj-shipping-heading" style={{
                        fontSize: 32, fontWeight: 700, marginBottom: 32, letterSpacing: '-0.5px'
                    }}>
                        SHIPPING OPTIONS
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 80 }}>
                        {shippingCards.map((card, idx) => (
                            <div key={idx} style={{
                                padding: 32, border: '1px solid #DEE2E6',
                                transition: 'border-color 0.2s, box-shadow 0.2s'
                            }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0D7C66'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(13,124,102,0.1)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#DEE2E6'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{
                                    width: 56, height: 56, background: '#E8F5F1', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', marginBottom: 20
                                }}>
                                    <card.icon size={26} color="#0D7C66" />
                                </div>
                                <h3 className="sahaj-shipping-heading" style={{
                                    fontSize: 18, fontWeight: 700, margin: '0 0 8px'
                                }}>
                                    {card.title}
                                </h3>
                                <p className="sahaj-shipping-heading" style={{
                                    fontSize: 14, fontWeight: 600, color: '#0D7C66', margin: '0 0 4px'
                                }}>
                                    {card.detail}
                                </p>
                                <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>{card.price}</p>
                                <p style={{ fontSize: 13, color: '#6C757D', margin: 0 }}>{card.sub}</p>
                            </div>
                        ))}
                    </div>

                    {/* Exchange Process */}
                    <h2 className="sahaj-shipping-heading" style={{
                        fontSize: 32, fontWeight: 700, marginBottom: 32, letterSpacing: '-0.5px'
                    }}>
                        RETURN & EXCHANGE PROCESS
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, marginBottom: 80 }}>
                        {exchangeSteps.map((step, idx) => (
                            <div key={idx} style={{
                                padding: 32,
                                borderLeft: idx > 0 ? '1px solid #DEE2E6' : 'none',
                                position: 'relative'
                            }}>
                                <span className="sahaj-shipping-heading" style={{
                                    fontSize: 48, fontWeight: 700, color: '#E8F5F1', display: 'block',
                                    lineHeight: 1, marginBottom: 16
                                }}>
                                    {step.num}
                                </span>
                                <h3 className="sahaj-shipping-heading" style={{
                                    fontSize: 18, fontWeight: 700, margin: '0 0 12px'
                                }}>
                                    {step.title}
                                </h3>
                                <p style={{ fontSize: 14, lineHeight: 1.7, color: '#6C757D', margin: 0 }}>
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Return Policy */}
                    <h2 className="sahaj-shipping-heading" style={{
                        fontSize: 32, fontWeight: 700, marginBottom: 32, letterSpacing: '-0.5px'
                    }}>
                        RETURN POLICY
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginBottom: 80 }}>
                        <div>
                            <h3 className="sahaj-shipping-heading" style={{
                                fontSize: 16, fontWeight: 700, color: '#0D7C66', marginBottom: 20,
                                textTransform: 'uppercase', letterSpacing: '1px'
                            }}>
                                Eligible for Return
                            </h3>
                            {policies.filter(p => p.allowed).map((policy, idx) => (
                                <div key={idx} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    marginBottom: 16, fontSize: 14, lineHeight: 1.5
                                }}>
                                    <CheckCircle size={18} color="#0D7C66" style={{ flexShrink: 0, marginTop: 1 }} />
                                    <span>{policy.text}</span>
                                </div>
                            ))}
                        </div>
                        <div>
                            <h3 className="sahaj-shipping-heading" style={{
                                fontSize: 16, fontWeight: 700, color: '#DC3545', marginBottom: 20,
                                textTransform: 'uppercase', letterSpacing: '1px'
                            }}>
                                Not Eligible
                            </h3>
                            {policies.filter(p => !p.allowed).map((policy, idx) => (
                                <div key={idx} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    marginBottom: 16, fontSize: 14, lineHeight: 1.5, color: '#6C757D'
                                }}>
                                    <AlertCircle size={18} color="#DC3545" style={{ flexShrink: 0, marginTop: 1 }} />
                                    <span>{policy.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Refund Info */}
                    <div style={{
                        background: '#F8F9FA', padding: 40, border: '1px solid #DEE2E6',
                        display: 'flex', gap: 32, alignItems: 'flex-start'
                    }}>
                        <div style={{
                            width: 56, height: 56, background: '#E8F5F1', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <IndianRupee size={26} color="#0D7C66" />
                        </div>
                        <div>
                            <h3 className="sahaj-shipping-heading" style={{ fontSize: 20, fontWeight: 700, margin: '0 0 12px' }}>
                                Refund Information
                            </h3>
                            <p style={{ fontSize: 15, lineHeight: 1.7, color: '#6C757D', margin: '0 0 16px' }}>
                                Refunds are processed to the original payment method within 7-10 business days after we receive and inspect the returned item. Bank processing times may add 3-5 additional business days. For COD orders, refunds are issued via bank transfer.
                            </p>
                            <span
                                onClick={() => navigate('/template25/contact')}
                                style={{ fontSize: 14, color: '#0D7C66', fontWeight: 600, cursor: 'pointer' }}
                            >
                                Have questions? Contact us →
                            </span>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default ShippingAndExchange25;
